/// <amd-module name="Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/TestStepsParseUtils" />

// Based on code in \Tfs\Service\WebAccess\TestManagement\Scripts\TFS.TestManagement.ts
import {
    TestStepChangeType,
    SharedSteps,
    TestAction,
    TestActionTypes,
    TestStep
} from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/TestStepOM";
import * as VSS_Utils_String from "VSS/Core/Util/String";

export interface ITestStepMetaData {
    id: number;
    action: any;
    expectedResult: any;
    type: TestActionTypes;
    stepNumber: number;
}

type IdToTestStepMap = { [id: number]: ITestStepMetaData };

export interface ITestStepChange {
    type: TestStepChangeType;
    newValue?: ITestStepMetaData;
    oldValue?: ITestStepMetaData;
}

// This reqex is used to match and replace certain HTML tags.
// This is necessary because the test step value contains data inside HTML tags that needs to be removed before comparing.
const valueCompareRegex = /<DIV>|<P>|&nbsp;|<\/DIV>|<\/P>/gi;
const emptyString = "";


/*
We need this class to parse the test steps as they come from the server as a string in XML format.

Sample steps to parse:
<steps id="0" last="8">
    <step id="2" type="ValidateStep">
        <parameterizedString isformatted="true">&lt;P&gt;step1 &amp;lt;script&amp;gt;i=0&amp;lt;/script&amp;gt;&amp;lt;p&amp;gt;MyPara&amp;lt;/p&amp;gt;&lt;BR/&gt;&amp;nbsp; &lt;/P&gt;&lt;P class="MsoNormal"&gt;&lt;SPAN style="font-size:24.0pt;line-height:107%;font-family: &amp;amp;amp"&gt;Abadi Font Size 24&lt;/SPAN&gt;&lt;/P&gt;&lt;P&gt;&lt;BR/&gt;&lt;/P&gt;</parameterizedString>
        <parameterizedString isformatted="true">&lt;DIV&gt;&lt;P&gt;exp1&lt;/P&gt;&lt;/DIV&gt;</parameterizedString>
        <description/>
    </step>
    <compref id="6" ref="17">
        <compref id="7" ref="18">
            <step id="5" type="ValidateStep">
                <parameterizedString isformatted="true">&lt;DIV&gt;&lt;P&gt;step4&lt;/P&gt;&lt;/DIV&gt;</parameterizedString>
                <parameterizedString isformatted="true">&lt;DIV&gt;&lt;P&gt;exp4&lt;/P&gt;&lt;/DIV&gt;</parameterizedString>
                <description/>
            </step>
            <step id="8" type="ValidateStep">
                <parameterizedString isformatted="true">&lt;DIV&gt;&lt;P&gt;step5&lt;/P&gt;&lt;/DIV&gt;</parameterizedString>
                <parameterizedString isformatted="true">&lt;DIV&gt;&lt;P&gt;exp5&lt;/P&gt;&lt;/DIV&gt;</parameterizedString>
                <description/>
            </step>
        </compref>
    </compref>
</steps>
*/
export class TestStepsParseUtils {
    public static getTestStepChanges(update: { newValue: any; oldValue: any }): ITestStepChange[] {
        let newSteps: TestAction[] = [];
        let oldSteps: TestAction[] = [];
        const parser = new DOMParser();
        const testStepsTag = "steps";

        let stepsXml = parser.parseFromString(update.newValue, "text/xml").querySelectorAll(testStepsTag);
        if (stepsXml.length > 0) {
            newSteps = this.parseTestSteps(stepsXml);
        }

        stepsXml = parser.parseFromString(update.oldValue, "text/xml").querySelectorAll(testStepsTag);
        if (stepsXml.length > 0) {
            oldSteps = this.parseTestSteps(stepsXml);
        }

        let newStepsIdMap: IdToTestStepMap = {};
        let oldStepsIdMap: IdToTestStepMap = {};

        this._generateTestStepsIdMap(newSteps, newStepsIdMap);
        this._generateTestStepsIdMap(oldSteps, oldStepsIdMap);

        let testStepChanges = this.addOrDeleteOrModifyTestSteps(newSteps, oldSteps, newStepsIdMap, oldStepsIdMap);
        if (testStepChanges.length === 0) {
            testStepChanges = this.parseTestStepsReordering(newSteps, oldSteps, newStepsIdMap, oldStepsIdMap);
        }

        return testStepChanges;
    }

    public static parseTestStepsReordering(
        newSteps: TestAction[],
        oldSteps: TestAction[],
        newStepsIdMap: IdToTestStepMap,
        oldStepsIdMap: IdToTestStepMap
    ): ITestStepChange[] {
        let testStepChanges: ITestStepChange[] = [];

        newSteps.forEach((newStep: TestAction, index: number) => {
            let testStepChange: ITestStepChange | undefined;
            if (newStep.id !== oldSteps[index].id) {
                testStepChange = {
                    type: TestStepChangeType.Edited,
                    newValue: newStepsIdMap[newStep.id],
                    oldValue: oldStepsIdMap[newStep.id]
                };

                this._updateTestStepChange(testStepChange);
            }

            if (testStepChange) {
                testStepChanges.push(testStepChange);
            }
        });

        return testStepChanges;
    }

    private static _generateTestStepsIdMap(steps: any, idMap: IdToTestStepMap): void {
        steps.forEach((step: any, index: number) => {
            let action = "",
                expectedResult = "";

            if (step.actionType === TestActionTypes.SharedSteps) {
                action = step.ref;
            } else {
                action = step.action;
                expectedResult = step.expectedResult;
            }

            idMap[step.id] = {
                id: step.id,
                action: action,
                expectedResult: expectedResult,
                type: step.actionType,
                stepNumber: index + 1
            };
        });
    }

    private static addOrDeleteOrModifyTestSteps(
        newSteps: TestAction[],
        oldSteps: TestAction[],
        newStepsIdMap: IdToTestStepMap,
        oldStepsIdMap: IdToTestStepMap
    ): ITestStepChange[] {
        const testStepChanges: ITestStepChange[] = [];

        // find out if new step modified or added
        newSteps.forEach((newStep: TestAction) => {
            let testStepChange: ITestStepChange | undefined;
            if (oldStepsIdMap[newStep.id]) {
                if (!this.compareTestSteps(newStepsIdMap[newStep.id], oldStepsIdMap[newStep.id])) {
                    testStepChange = {
                        type: TestStepChangeType.Edited,
                        newValue: newStepsIdMap[newStep.id],
                        oldValue: oldStepsIdMap[newStep.id]
                    };

                    this._updateTestStepChange(testStepChange);
                }
            } else {
                testStepChange = {
                    type: TestStepChangeType.Added,
                    newValue: newStepsIdMap[newStep.id]
                };
            }

            if (testStepChange) {
                testStepChanges.push(testStepChange);
            }
        });

        // find out if step deleted
        oldSteps.forEach((oldStep: any) => {
            let testStepChange: ITestStepChange | undefined;
            if (!newStepsIdMap[oldStep.id]) {
                testStepChange = {
                    type: TestStepChangeType.Deleted,
                    oldValue: oldStepsIdMap[oldStep.id]
                };
            }

            if (testStepChange) {
                testStepChanges.push(testStepChange);
            }
        });

        return testStepChanges;
    }

    private static _updateTestStepChange(testStepChange: ITestStepChange): void {
        if (!testStepChange.newValue || !testStepChange.oldValue) {
            return;
        }

        if (this._compareRegexStrings(testStepChange.newValue.action.toString(), testStepChange.oldValue.action.toString())) {
            testStepChange.newValue.action = emptyString;
            testStepChange.oldValue.action = emptyString;
        }

        if (this._compareRegexStrings(testStepChange.newValue.expectedResult.toString(), testStepChange.oldValue.expectedResult.toString())) {
            testStepChange.newValue.expectedResult = emptyString;
            testStepChange.oldValue.expectedResult = emptyString;
        }
    }

    public static compareTestSteps(step1: ITestStepMetaData, step2: ITestStepMetaData): boolean {
        return (
            step1.type === step2.type &&
            this._compareRegexStrings(step1.action.toString(), step2.action.toString()) &&
            this._compareRegexStrings(step1.expectedResult.toString(), step2.expectedResult.toString())
        );
    }

    private static _compareRegexStrings(string1: string, string2: string): boolean {
        return string1.replace(valueCompareRegex, emptyString) === string2.replace(valueCompareRegex, emptyString);
    }

    public static parseTestSteps(steps: NodeListOf<Element>): TestAction[] {
        function readParameterizedString(parameterizedStringElement: Element) {
            let stringParts = [];
            if (parameterizedStringElement.children.length > 0) {
                for (let i = 0; i < parameterizedStringElement.children.length; i++) {
                    let parameterizedElementDetail: Element = parameterizedStringElement.children[i];
                    switch (parameterizedElementDetail.nodeName.toLowerCase()) {
                        case "parameter":
                            stringParts.push(VSS_Utils_String.format("@{0}", parameterizedElementDetail.textContent));
                            break;
                        case "outputparameter":
                            stringParts.push(VSS_Utils_String.format("@?{0}", parameterizedElementDetail.textContent));
                            break;
                        case "text":
                            stringParts.push(parameterizedElementDetail.textContent);
                            break;
                    }
                }
            } else {
                stringParts.push(parameterizedStringElement.textContent);
            }

            return stringParts.join("");
        }

        function readStep(step: Element): TestStep {
            let id = step.getAttribute("id"),
                stepType = step.getAttribute("type"),
                action,
                expectedResult,
                testStep,
                isFormatted = false,
                isActionFormatted = false,
                isExpectedResultFormatted = false;

            //The first string is the action and second is the expected result
            let stepDetails: NodeListOf<Element> = step.querySelectorAll(":scope > parameterizedString");
            for (let index = 0; index < stepDetails.length; index++) {
                if (index === 0) {
                    action = readParameterizedString(stepDetails[index]);
                    isActionFormatted = stepDetails[index].getAttribute("isformatted") === "true";
                } else {
                    expectedResult = readParameterizedString(stepDetails[index]);
                    isExpectedResultFormatted = stepDetails[index].getAttribute("isformatted") === "true";
                }
            }

            if (isActionFormatted && isExpectedResultFormatted) {
                isFormatted = true;
            }

            if (!action && !expectedResult) {
                // It might be a test case created with the pre Dev10 Beta 1 format.
                // Try loading it.
                let stepDetails: HTMLCollectionOf<Element> = step.children;
                for (let i = 0; i < stepDetails.length; i++) {
                    let stepDetail = stepDetails[i];
                    switch (stepDetail.nodeName.toLowerCase()) {
                        case "action":
                            action = stepDetail.textContent;
                            break;
                        case "expected":
                            expectedResult = stepDetail.textContent;
                            break;
                    }
                }
            }

            if (id === null || stepType === null) {
                throw new Error("id or stepType is not present");
            }

            //create the test step
            testStep = TestStep.createStep(parseInt(id!, 10), stepType, action ?? "", expectedResult ?? "", isFormatted);

            return testStep;
        }

        function readSharedSteps(step: Element): TestAction[] {
            let id = step.getAttribute("id"),
                ref = step.getAttribute("ref"),
                steps: TestAction[] = [],
                innerStep,
                innerSteps: TestAction[] = [];

            steps.push(SharedSteps.createSharedStep(parseInt(id!, 10), parseInt(ref!, 10)));

            Array.from(step.children).forEach((stepDetailElement: Element) => {
                switch (stepDetailElement.nodeName.toLowerCase()) {
                    case "step":
                        innerStep = readStep(stepDetailElement);
                        innerSteps.push(innerStep);
                        break;
                    case "compref":
                        innerStep = readSharedSteps(stepDetailElement);
                        //can be many steps
                        innerSteps = innerSteps.concat(innerStep);
                        break;
                }
            });

            if (innerSteps && innerSteps.length > 0) {
                steps = steps.concat(innerSteps);
            }
            return steps;
        }

        function _parseSteps($steps: NodeListOf<Element>): TestAction[] {
            let step,
                steps: TestAction[] = [],
                stepElements: HTMLCollectionOf<Element>,
                stepDetailElement: Element;
            for (let i = 0; i < $steps.length; i++) {
                stepElements = $steps[i].children;
                for (let j = 0; j < stepElements.length; j++) {
                    stepDetailElement = stepElements[j];
                    switch (stepDetailElement.nodeName.toLowerCase()) {
                        case "step":
                            step = readStep(stepElements[j]);
                            steps.push(step);
                            break;
                        case "compref":
                            step = readSharedSteps(stepElements[j]);
                            //can be many steps
                            steps = steps.concat(step);
                            break;
                    }
                }
            }
            return steps;
        }

        return _parseSteps(steps);
    }
}
